import posthog from "posthog-js";

import { ga4Event } from "./ga4";
import { scrubProperties } from "./pii";

/*
 * The Apex 25 funnel events, sent to PostHog and GA4 with the same names and properties.
 * page_viewed goes out as each tool's native page view ($pageview in PostHog, page_view in GA4)
 * so their built-in page reports keep working.
 *
 * Properties never carry personal data: scrubProperties removes anything that looks like it.
 */
export type FunnelEvents = {
  page_viewed: { page_route: string; page_title?: string };
  cta_clicked: { cta_id: string; cta_location: string; destination?: string };
  trial_started: { trial: "interview_demo"; role?: string };
  trial_completed: { trial: "interview_demo"; role?: string; reason?: string };
  email_captured: { source: string };
  signup_completed: { method?: string };
  demo_booked: { source?: string };
  checkout_started: {
    item_type: "plan" | "pack";
    item_id?: string;
    item_name?: string;
    value?: number;
    currency?: string;
  };
  payment_completed: { transaction_id?: string; success: boolean };
};

export type FunnelEvent = keyof FunnelEvents;

const cleanPageLocation = () => {
  const url = new URL(window.location.href);
  const kept = new URLSearchParams();
  url.searchParams.forEach((value, key) => {
    if (key.startsWith("utm_") || key === "gclid") kept.set(key, value);
  });
  const query = kept.toString();
  return `${url.origin}${url.pathname}${query ? `?${query}` : ""}`;
};

const capture = (event: string, properties: Record<string, unknown>) => {
  try {
    if (posthog.__loaded) posthog.capture(event, properties);
  } catch {
    // Analytics must never break the page.
  }
};

export const trackEvent = <E extends FunnelEvent>(event: E, properties: FunnelEvents[E]) => {
  const clean = scrubProperties(properties);

  if (event === "page_viewed") {
    capture("$pageview", clean);
    ga4Event("page_view", {
      page_location: cleanPageLocation(),
      page_title: clean.page_title,
      page_route: clean.page_route,
    });
    return;
  }

  capture(event, clean);
  ga4Event(event, clean);
};

/** Destination for cta_clicked: host + path only. */
export const ctaDestination = (href: string) => {
  try {
    const url = new URL(href, window.location.href);
    return `${url.host}${url.pathname}`;
  } catch {
    return undefined;
  }
};
